// ── Bugify "Deploy now to all systems" (owner 2026-11-03) ───────────────────────────────────────────────────────────
// 「在点击confirm按钮的时候弹出一个界面询问是立即部署到所有租户（可能需要重启系统）？还是晚上8点之后全自动部署？…注意部署时，
// 增加等待进度条，确保客户看起来不是系统死机，用户知道当前正在部署。」
// Confirm now asks when the fix should go out to the other systems: after 20:00 tonight (the default, as before), or
// right now — one system after another, each restarting for about a minute. The server keeps one record per deploy
// (bugify_releases; GET /api/bugify/release, server/src/services/bugifyRelease.ts) and the release machine reports into
// it as it goes, so what is drawn here is the real thing: which system, which step, how far, how long.
//   • useRelease      — polls that record: every 3 s while a deploy is waiting or running, every 30 s otherwise, not
//                       at all while the tab is hidden.
//   • ConfirmRelease  — the question Confirm asks.
//   • ReleaseCard     — a confirmed fix's deploy, on the report: a waiting card, then a bar that moves, a target list,
//                       and how it ended.
//   • ReleaseBanner   — the same in one line across the top of the list while a deploy is on.
// Nothing here ever stands still: every wait has a moving bar and a counter redrawn each second from the browser's own
// clock (set to the server's), and nothing is fetched more often than the poll above.
// ⛔ Never imports ./index.tsx (it imports this file).
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { api } from '../../utils/api';
import { tz } from '../../utils/region';
import { fmtElapsed } from '../../utils/deployStatus';

// ── The shared shapes (server: GET /api/bugify/release) ─────────────────────────────────────────────────────────────
export type Step = '' | 'start' | 'lock' | 'gates' | 'build' | 'tsc' | 'upload' | 'restart' | 'verify' | 'health';
export type ReleaseState = 'requested' | 'running' | 'held' | 'done' | 'failed' | 'blocked' | 'expired' | 'lost' | 'cancelled';
export type ReleaseTarget = {
  name: string; state: 'pending' | 'running' | 'done' | 'current' | 'failed' | 'held';
  step: Step; started_at: number; finished_at: number; error: string; code: string;
};
export type ReleaseView = {
  id: string; mode: 'now' | 'night'; state: ReleaseState; attempt: string;
  stalled: boolean; quiet_secs: number; slow: boolean;
  requested_by_name: string; urgent: boolean;
  requested_at: number; expires_at: number; not_before: number;
  started_at: number; finished_at: number; updated_at: number; retry_at: number;
  head: string; item_ids: string[];
  phase: '' | 'starting' | 'checks' | 'worktree' | 'deploying' | 'finishing';
  targets: ReleaseTarget[];
  current_target: string; step: Step; step_started_at: number; step_eta: number;
  pct: number; pct_next: number; remaining: number; deployed: string[];
  note: string; code: string; error: string; failed_target: string; part_updated: boolean;
  stop_requested_by_name: string;
};
export type ReleaseInfo = {
  server_now: number; night_now: boolean; has_releaser: boolean; releaser_seen_secs: number;
  followers: string[]; owed: number; riding: number;
  test_live: { sha: string; age_secs: number } | null;
  last_failure: { at: number; target: string; code: string; error: string } | null;
  can: { now: boolean; now_code: string; not_before: number; stop: boolean; privileged: boolean };
  pending_now: boolean;
  current: ReleaseView | null;
  next: ReleaseView | null;
};
export type ReleaseHook = { info: ReleaseInfo | null; error: boolean; refresh: () => Promise<void>; fast: () => Promise<void> };

// ── The clock ───────────────────────────────────────────────────────────────────────────────────────────────────────
// Every time on the record is the server's. The difference to this browser's clock is taken from each answer, and only
// replaced when it moves by more than 2 s — a request's own travel time would otherwise make the counters stutter.
let offsetSecs = 0, offsetSeeded = false;
const nowSecs = (): number => Date.now() / 1000 + offsetSecs;

const ACTIVE: ReleaseState[] = ['requested', 'held', 'running'];
const isActive = (v: ReleaseView | null | undefined): boolean => !!v && ACTIVE.includes(v.state);
const POLL_BUSY_MS = 3000, POLL_IDLE_MS = 30_000, FAST_MS = 60_000;

/** The deploy record, polled. 3 s while a deploy waits or runs (or for a minute after fast()), else 30 s; nothing
 *  while the tab is hidden, and at once when it comes back. refresh() and fast() resolve once that answer is in, so a
 *  screen can redraw the report and its deploy together instead of one second apart. */
export function useRelease(): ReleaseHook {
  const [info, setInfo] = useState<ReleaseInfo | null>(null);
  const [error, setError] = useState(false);
  const kick = useRef<() => Promise<void>>(() => Promise.resolve());
  const fastUntil = useRef(0);
  useEffect(() => {
    let alive = true, timer: number | undefined, seq = 0, last: ReleaseInfo | null = null;
    const plan = () => {
      window.clearTimeout(timer);
      if (!alive || document.visibilityState === 'hidden') return;
      const busy = isActive(last?.current) || isActive(last?.next) || Date.now() < fastUntil.current;
      timer = window.setTimeout(() => { void run(); }, busy ? POLL_BUSY_MS : POLL_IDLE_MS);
    };
    const run = (): Promise<void> => {
      window.clearTimeout(timer);
      if (!alive || document.visibilityState === 'hidden') return Promise.resolve();   // the visibility listener starts it again
      const mine = ++seq;   // a newer request (refresh) supersedes this one, answer and schedule alike
      return api.bugifyRelease()
        .then((d: ReleaseInfo) => {
          if (!alive || mine !== seq) return;
          const off = Number(d?.server_now) - Date.now() / 1000;
          if (isFinite(off) && (!offsetSeeded || Math.abs(off - offsetSecs) > 2)) { offsetSecs = off; offsetSeeded = true; }
          last = d; setInfo(d); setError(false);
        })
        .catch(() => { if (alive && mine === seq) setError(true); })
        .finally(() => { if (alive && mine === seq) plan(); });
    };
    kick.current = run;
    const onVisible = () => { if (document.visibilityState === 'visible') void run(); };
    document.addEventListener('visibilitychange', onVisible);
    void run();
    return () => {
      alive = false;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      kick.current = () => Promise.resolve();
    };
  }, []);
  const refresh = useCallback(() => kick.current(), []);
  const fast = useCallback(() => { fastUntil.current = Date.now() + FAST_MS; return kick.current(); }, []);
  return { info, error, refresh, fast };
}

/** Changes when the deploy on show starts, ends or tries again — the moment the reports' own words change too.
 *  '' until the first answer, which is a baseline, not a change. */
export const releaseSignal = (info: ReleaseInfo | null): string => {
  if (!info) return '';
  const c = info.current;
  return `${c?.id}:${c?.state}:${c?.attempt}`;
};

// ── Words ───────────────────────────────────────────────────────────────────────────────────────────────────────────
/** "A, B and C" — the systems' names joined the way the reader's language joins a list. */
export function listOf(names: string[], lang: string): string {
  const clean = (names || []).filter(Boolean);
  try {
    const LF = (Intl as any).ListFormat;
    if (LF) return new LF(lang, { type: 'conjunction' }).format(clean);
  } catch { /* an old browser, or a language it does not know */ }
  return clean.join(', ');
}
/** The other systems by name, from the release machine's own list; generic words when it has not reported one. */
export const systemsOf = (info: ReleaseInfo | null, t: TFunction, lang: string, startOfSentence = false): string =>
  info?.followers?.length ? listOf(info.followers, lang) : startOfSentence ? t('bgr_systems_unknown') : t('bgr_systems_unknown_mid');

/** A server error as a sentence: the code's own words, else the server's English, else a generic one. */
export function releaseErrorText(e: any, t: TFunction, exists: (k: string) => boolean): string {
  const code = String(e?.payload?.code || '');
  if (code && exists(`bgr_err_${code}`)) return t(`bgr_err_${code}`);
  if (e?.payload?.error) return String(e.payload.error);
  if (e?.apiDown) return t('sysup_try_again');
  return t('bgr_err_generic');
}

/** Why "Deploy now" cannot be pressed right now, as a sentence. 'now_unavailable' covers three different causes, and
 *  the person who sees the owner's option greyed out should be told which one. */
function nowUnavailableText(info: ReleaseInfo, t: TFunction, exists: (k: string) => boolean): string {
  const code = info.can.now_code;
  const seen = info.releaser_seen_secs;
  if (code === 'now_unavailable') {
    if (seen < 0) return t('bgr_now_unavail_never');
    if (!info.followers?.length) return t('bgr_now_unavail_followers');
    if (seen >= 120) return t('bgr_now_unavail_offline', { min: minsOf(seen) });
  }
  return code && exists(`bgr_err_${code}`) ? t(`bgr_err_${code}`) : t('bgr_err_now_unavailable');
}

const clockOf = (secs: number, lang: string): string => {
  const d = new Date(secs * 1000);
  try { return new Intl.DateTimeFormat(lang, { timeZone: tz(), hour: '2-digit', minute: '2-digit' }).format(d); }
  catch { return new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }).format(d); }
};
const minsOf = (secs: number): number => Math.max(1, Math.round(secs / 60));

// ── Look ────────────────────────────────────────────────────────────────────────────────────────────────────────────
type Look = { fg: string; bg: string };
const LOOK: Record<'purple' | 'amber' | 'green' | 'red', Look> = {
  purple: { fg: '#6D28D9', bg: '#F5F3FF' },
  amber:  { fg: '#B45309', bg: '#FFFBEB' },
  green:  { fg: '#15803D', bg: '#E8F6EE' },
  red:    { fg: '#B91C1C', bg: '#FEF2F2' },
};
const lookOf = (v: ReleaseView, queued = false): Look =>
  queued ? LOOK.amber : v.state === 'running' ? LOOK.purple : v.state === 'done' ? LOOK.green
    : v.state === 'failed' || v.state === 'lost' ? LOOK.red : LOOK.amber;
const srOnly: React.CSSProperties = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };

/** Redraws once a second while `on`: the counters and the bar's creep between two answers. */
function useTick(on: boolean): void {
  const [, set] = useState(0);
  useEffect(() => {
    if (!on) return;
    const id = window.setInterval(() => set(n => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [on]);
}

// ── Reading a record ────────────────────────────────────────────────────────────────────────────────────────────────
function useWords() {
  const { t, i18n } = useTranslation();
  const exists = (k: string) => i18n.exists(k);
  const step = (s: Step | string): string => (s && exists(`bgr_step_${s}`) ? t(`bgr_step_${s}`) : t('bgr_step_start'));
  /** Why it is waiting / stopped, in words: the reason code's, else what the machine said, else "not picked up". */
  const reason = (v: ReleaseView): string =>
    v.code && exists(`bgr_why_${v.code}`) ? t(`bgr_why_${v.code}`) : v.note || v.error || t('bgr_why_not_picked');
  const names = (list: string[]): string => (list?.length ? listOf(list, i18n.language) : t('bgr_none'));
  return { t, i18n, exists, step, reason, names, lang: i18n.language };
}

const runningTarget = (v: ReleaseView): ReleaseTarget | undefined => (v.targets || []).find(x => x.state === 'running');

/** The moving bar's figure: the server's pct, crept towards pct_next as the step's usual time passes, never back. */
const lastShown = new Map<string, number>();
function shownPct(v: ReleaseView): number {
  const key = `${v.id}:${v.attempt}`;
  const from = Math.max(0, Number(v.pct) || 0), to = Math.max(from, Number(v.pct_next) || 0);
  const t = v.step_started_at ? Math.max(0, nowSecs() - v.step_started_at) : 0;
  const est = from + (to - from) * 0.95 * (1 - Math.exp(-t / Math.max(1, v.step_eta || 10)));
  const shown = Math.min(99, Math.max(lastShown.get(key) || 0, est));
  if (lastShown.size > 50) lastShown.clear();
  lastShown.set(key, shown);
  return shown;
}

/** The waiting/running/ending sentence — also what a screen reader hears when it changes. */
function headline(v: ReleaseView, info: ReleaseInfo, w: ReturnType<typeof useWords>, queued: boolean): string {
  const { t, reason, names } = w;
  if (queued) return t('bgr_waiting');
  switch (v.state) {
    case 'requested': return t('bgr_waiting');
    case 'held': return v.mode === 'night' ? t('bgr_held_night', { reason: reason(v) }) : t('bgr_held', { reason: reason(v) });
    case 'running': {
      if (v.phase === 'finishing') return t('bgr_finishing');
      if (v.phase !== 'deploying' || !v.targets?.length) return t('bgr_preparing');
      const r = runningTarget(v);
      return r ? t('bgr_running', { system: r.name, step: w.step(r.step || v.step) }) : t('bgr_between');
    }
    case 'done':
      if (v.remaining > 0) return t('bgr_done_partial', { names: names(v.deployed) });
      if (!v.deployed?.length) return t('bgr_done_noop');
      return t('bgr_done', { time: fmtElapsed(Math.max(0, (v.finished_at || nowSecs()) - (v.started_at || v.requested_at)) * 1000) });
    case 'blocked':
      if (v.code === 'check_only') return t('bgr_checked');
      if (v.code === 'stopped') return t('bgr_stopped', { who: v.stop_requested_by_name || v.requested_by_name || '—', names: names(v.deployed) });
      return t('bgr_blocked', { reason: reason(v) });
    case 'failed':
      return v.failed_target ? t('bgr_failed', { system: v.failed_target, error: reason(v) }) : t('bgr_failed_early', { error: reason(v) });
    case 'expired':
      return info.night_now ? t('bgr_expired_night', { reason: reason(v) }) : t('bgr_expired', { reason: reason(v) });
    case 'lost':
      return info.night_now ? t('bgr_lost_night') : t('bgr_lost');
    default: return '';
  }
}

/** Seconds the counter shows: a person's wait since they asked, a run since it started, or how long a finished one
 *  took (a night hold is finished: it is the end of that night's attempt). */
function elapsedSecs(v: ReleaseView): number {
  const end = v.finished_at || nowSecs();
  const start = v.mode === 'now' && (v.state === 'requested' || v.state === 'held') ? v.requested_at : v.started_at || v.requested_at;
  return Math.max(0, end - (start || end));
}

function Bar({ look, pct, label, valueText, slim }: { look: Look; pct: number | null; label: string; valueText?: string; slim?: boolean }) {
  const h = slim ? 4 : 8;
  if (pct === null) {
    // Nothing to measure yet: a sweep across the whole bar, so the wait still visibly moves.
    return (
      <div aria-hidden style={{ position: 'relative', height: h, borderRadius: 999, background: `${look.fg}22`, overflow: 'hidden' }}>
        <div className="mf-sweep" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '40%', borderRadius: 999, background: look.fg, opacity: 0.6, overflow: 'hidden' }}>
          <div className="mf-stripe" style={{ width: '100%', height: '100%' }} />
        </div>
      </div>
    );
  }
  const v = Math.max(0, Math.min(100, pct));
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.floor(v)} aria-valuetext={valueText}
      style={{ height: h, borderRadius: 999, background: `${look.fg}22`, overflow: 'hidden' }}>
      <div className={v < 100 ? 'mf-stripe' : undefined} style={{ width: `${v}%`, height: '100%', borderRadius: 999, background: look.fg, transition: 'width 1s linear' }} />
    </div>
  );
}

// ── The Confirm question ────────────────────────────────────────────────────────────────────────────────────────────
/** The body of the Confirm modal (the frame and its title are Bugify's own Modal). `onChoose()` with no mode is the
 *  plain Confirm of a server that offers no choice — the fix then goes out with the nightly release, as before. */
export function ConfirmRelease({ info, error, busy, err, onChoose, onCancel }: {
  info: ReleaseInfo | null; error: boolean; busy: boolean; err: string;
  onChoose: (mode?: 'now' | 'tonight') => void; onCancel: () => void;
}) {
  const w = useWords();
  const { t, lang } = w;
  const tonightId = useId(), nowId = useId();
  // The choices load with the modal (refresh on open); five seconds with no answer and it falls back to a plain
  // Confirm rather than leave a spinner where the buttons should be.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => { const id = window.setTimeout(() => setTimedOut(true), 5000); return () => window.clearTimeout(id); }, []);

  const footer = (confirm: boolean, confirmDisabled = false) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 16 }}>
      {busy && <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginRight: 'auto' }}><i className="ti ti-loader-2 mf-spin" style={{ marginRight: 6 }} aria-hidden />{t('bgr_submitting')}</span>}
      <button onClick={onCancel} disabled={busy} style={{ ...btn, padding: '7px 13px', fontSize: 13, opacity: busy ? 0.5 : 1 }}>{t('bg_cancel')}</button>
      {confirm && (
        <button onClick={() => onChoose()} disabled={busy || confirmDisabled} autoFocus
          style={{ ...btn, padding: '7px 13px', fontSize: 13, background: LOOK.green.fg, color: '#fff', border: 'none', opacity: busy || confirmDisabled ? 0.5 : 1 }}>
          <i className="ti ti-check" aria-hidden />{t('bg_confirm')}
        </button>
      )}
    </div>
  );
  const errBox = err ? <div role="alert" style={{ marginTop: 12, background: LOOK.red.bg, color: LOOK.red.fg, border: `1px solid ${LOOK.red.fg}33`, borderRadius: 8, padding: '8px 11px', fontSize: 12.5 }}>{err}</div> : null;
  const text: React.CSSProperties = { fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-primary)' };

  if (!info && !error && !timedOut) {
    return (
      <div>
        <div style={{ ...text, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-tertiary)' }}>
          <i className="ti ti-loader-2 mf-spin" style={{ fontSize: 18 }} aria-hidden />{t('bgr_modal_lead')}
        </div>
        {footer(true, true)}
      </div>
    );
  }
  // Only when there is no answer at all. A failed BACKGROUND poll keeps the last good answer (useRelease) — common on the
  // Bugify host, which restarts whenever a fix deploys — and the server re-checks "now" anyway, its refusal landing in
  // errBox. Falling back on any failed poll hid the owner's question behind an autofocused plain Confirm (= tonight).
  if (!info) return <div><div style={text}>{t('bgr_options_unavailable')}</div>{errBox}{footer(true)}</div>;
  if (!info.has_releaser) return <div><div style={text}>{t('bgr_confirm_only')}</div>{errBox}{footer(true)}</div>;

  const seen = info.releaser_seen_secs;
  const running = info.current?.state === 'running';
  const later = info.can.not_before > info.server_now;
  const notes: { icon: string; text: string; warn?: boolean }[] = [];
  if (info.can.now) {
    if (seen >= 0 && seen < 120 && !running && !later) notes.push({ icon: 'clock', text: t('bgr_waiting_hint') });
    if (seen >= 120) notes.push({ icon: 'plug-connected-x', text: t('bgr_offline_warn', { min: minsOf(seen) }), warn: true });
    if (running) notes.push({ icon: 'hourglass', text: t('bgr_busy_warn') });
    if (later) notes.push({ icon: 'clock-pause', text: t('bgr_not_before', { time: clockOf(info.can.not_before, lang) }), warn: true });
    if (info.last_failure) notes.push({ icon: 'alert-triangle', warn: true,
      text: t('bgr_failed_recent_warn', { system: info.last_failure.target || t('bgr_systems_unknown_mid'), min: minsOf(info.server_now - info.last_failure.at) }) });
    if (info.test_live && info.test_live.age_secs < 600) notes.push({ icon: 'git-commit', text: t('bgr_fresh_warn', { min: minsOf(info.test_live.age_secs) }) });
    if (info.riding > 0) notes.push({ icon: 'stack-2', text: t('bgr_riding', { n: info.riding }) });
  }
  const option = (o: { id: string; mode: 'now' | 'tonight'; look: Look; icon: string; title: string; hint: string; disabled?: boolean; first?: boolean; extra?: React.ReactNode }) => (
    <div style={{ marginTop: o.first ? 0 : 10 }}>
      <button onClick={() => onChoose(o.mode)} disabled={busy || o.disabled} autoFocus={o.first} aria-describedby={o.extra ? `${o.id}-hint ${o.id}-notes` : `${o.id}-hint`}
        style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 11, padding: '11px 13px', borderRadius: 10, cursor: busy || o.disabled ? 'not-allowed' : 'pointer',
          border: `1.5px solid ${o.look.fg}55`, background: o.look.bg, color: o.look.fg, opacity: busy || o.disabled ? 0.55 : 1 }}>
        <i className={`ti ti-${o.icon}`} style={{ fontSize: 20, marginTop: 1, flexShrink: 0 }} aria-hidden />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 800 }}>{o.title}</span>
          <span id={`${o.id}-hint`} style={{ display: 'block', fontSize: 12.5, lineHeight: 1.5, marginTop: 3, color: 'var(--text-secondary)' }}>{o.hint}</span>
        </span>
      </button>
      {o.extra}
    </div>
  );

  return (
    <div>
      <div style={{ ...text, marginBottom: 14 }}>{t('bgr_modal_lead')}</div>
      {option({ id: tonightId, mode: 'tonight', look: LOOK.green, icon: info.night_now ? 'moon-stars' : 'moon', first: true,
        title: info.night_now ? t('bgr_opt_tonight_night') : t('bgr_opt_tonight'),
        hint: info.night_now ? t('bgr_opt_tonight_night_hint') : t('bgr_opt_tonight_hint') })}
      {option({ id: nowId, mode: 'now', look: LOOK.purple, icon: 'rocket', disabled: !info.can.now,
        title: t('bgr_opt_now'),
        hint: t('bgr_opt_now_hint', { systems: systemsOf(info, t, lang, true) }),
        extra: (
          <div id={`${nowId}-notes`} style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 2 }}>
            {!info.can.now && (
              <div style={{ fontSize: 12.5, color: LOOK.amber.fg }}>
                <i className="ti ti-info-circle" style={{ marginRight: 5 }} aria-hidden />
                {nowUnavailableText(info, t, w.exists)}
              </div>
            )}
            {notes.map(n => (
              <div key={n.icon} style={{ fontSize: 12, lineHeight: 1.5, color: n.warn ? LOOK.amber.fg : 'var(--text-tertiary)', display: 'flex', gap: 5 }}>
                <i className={`ti ti-${n.icon}`} style={{ marginTop: 2, flexShrink: 0 }} aria-hidden /><span>{n.text}</span>
              </div>
            ))}
          </div>
        ) })}
      {errBox}
      {footer(false)}
    </div>
  );
}

// ── "Deploy now" for a fix already confirmed for tonight ────────────────────────────────────────────────────────────
/** Under "goes out after 20:00 tonight" on a confirmed report: the same immediate deploy, for someone who changed their
 *  mind. Drawn only while it can be done and nobody has asked for it yet. */
export function DeployNowButton({ info, onDeployNow }: { info: ReleaseInfo | null; onDeployNow: () => void }) {
  const { t, lang } = useWords();
  if (!info || !info.has_releaser || !info.can.now || info.pending_now) return null;
  return (
    <button onClick={() => { if (window.confirm(t('bgr_now_confirm', { systems: systemsOf(info, t, lang) }))) onDeployNow(); }}
      style={{ ...btn, marginTop: 8, color: LOOK.purple.fg, borderColor: `${LOOK.purple.fg}55` }}>
      <i className="ti ti-rocket" aria-hidden />{t('bgr_deploy_now_btn')}
    </button>
  );
}

// ── The deploy, on a confirmed report ───────────────────────────────────────────────────────────────────────────────
export function ReleaseCard({ itemId, info, onDeployNow, onStop }: {
  itemId: string; info: ReleaseInfo | null; onDeployNow: () => void; onStop: (releaseId: string) => void;
}) {
  const w = useWords();
  const { t, lang } = w;
  const cur = info?.current && (info.current.item_ids || []).includes(itemId) ? info.current : null;
  const nxt = !cur && info?.next && (info.next.item_ids || []).includes(itemId) ? info.next : null;
  const v = cur || nxt;
  // A row in `next` waits behind the running one: it reads as waiting, whatever it is.
  const queued = !!nxt;
  useTick(!!v && isActive(v));
  if (!info || !v || v.state === 'cancelled') return null;

  const look = lookOf(v, queued);
  const line = headline(v, info, w, queued);
  const running = v.state === 'running' && !queued;
  const waiting = queued || v.state === 'requested' || v.state === 'held';
  const r = runningTarget(v);
  const count = v.targets?.length || 0;
  const pos = r ? v.targets.indexOf(r) + 1 : Math.min(count, (v.targets || []).filter(x => x.state !== 'pending' && x.state !== 'running').length + 1);
  const pct = running ? shownPct(v) : v.state === 'done' ? 100 : null;
  const elapsed = fmtElapsed(elapsedSecs(v) * 1000);
  const ended = !isActive(v);
  const canRetry = ended && ['blocked', 'failed', 'expired', 'lost'].includes(v.state) && info.owed > 0 && info.can.now;
  const seen = info.releaser_seen_secs;
  const muted: React.CSSProperties = { fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-secondary)', marginTop: 4 };
  const stopSystem = v.current_target || r?.name || t('bgr_systems_unknown_mid');

  // What waits for what, under the headline.
  const later = waiting && v.not_before > nowSecs();
  // "Try again now" is only named when its button is there; otherwise the reason it is not.
  const noRetryWhy = ended && !canRetry && info.owed > 0 && !info.can.now ? nowUnavailableText(info, t, w.exists) : '';
  const sub: string[] = [];
  if (queued) sub.push(t('bgr_busy_warn'));
  else if (v.state === 'requested') {
    // Not "usually within a minute" when it is spaced to a later time — the line below says when.
    if (seen >= 0 && seen < 120) { if (!later) sub.push(t('bgr_waiting_hint')); }
    else if (seen >= 120) sub.push(t('bgr_offline_warn', { min: minsOf(seen) }));
  }
  if (later) sub.push(t('bgr_not_before', { time: clockOf(v.not_before, lang) }));
  if (v.state === 'failed') {
    if (v.part_updated && v.failed_target) sub.push(t('bgr_failed_partial', { system: v.failed_target }));
    sub.push(canRetry ? t('bgr_failed_hint') : t('bgr_failed_hint_noretry'));
    if (noRetryWhy) sub.push(noRetryWhy);
  }
  if (v.state === 'blocked' && v.code !== 'check_only' && v.code !== 'stopped') {
    sub.push(canRetry ? t('bgr_blocked_hint') : t('bgr_blocked_hint_noretry'));
    if (noRetryWhy) sub.push(noRetryWhy);
  }
  if (running && v.stalled) sub.push(t('bgr_stalled', { min: minsOf(nowSecs() - v.updated_at) }));
  if (running && v.slow) sub.push(t('bgr_slow'));

  const icon = waiting ? 'loader-2 mf-spin' : running ? 'loader-2 mf-spin' : v.state === 'done' ? 'circle-check'
    : v.state === 'failed' || v.state === 'lost' ? 'alert-octagon' : 'player-pause';

  return (
    <div style={{ background: look.bg, border: `1px solid ${look.fg}33`, color: look.fg, borderRadius: 10, padding: '11px 13px', fontSize: 13, marginBottom: 14 }}>
      {/* What changed, said once — not the counters, which move every second. */}
      <div aria-live="polite" style={srOnly}>{line}</div>
      {v.mode === 'night' && <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}><i className="ti ti-moon" style={{ marginRight: 4 }} aria-hidden />{t('bgr_nightly')}</div>}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontWeight: 700 }}>
        <i className={`ti ti-${icon}`} style={{ fontSize: 16, marginTop: 1, flexShrink: 0 }} aria-hidden />
        <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{line}</span>
      </div>
      {sub.map((s, k) => <div key={k} style={muted}>{s}</div>)}

      {(waiting || running || v.state === 'done') && (
        <div style={{ marginTop: 9 }}>
          <Bar look={look} pct={pct} label={t('bgr_progress_label')} valueText={line} />
        </div>
      )}
      <div aria-hidden style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', marginTop: 6, fontSize: 12, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
        {running && <span style={{ fontWeight: 800, color: look.fg }}>{Math.floor(pct || 0)}%</span>}
        {running && v.phase === 'deploying' && count > 0 && <span>{t('bgr_of', { i: pos, n: count })}</span>}
        <span>{t('bgr_elapsed', { time: elapsed })}</span>
        {v.mode === 'now' && v.requested_by_name && <span>{t('bgr_requested_by', { who: v.requested_by_name })}</span>}
      </div>

      {count > 0 && (running || ended) && (
        <ul style={{ listStyle: 'none', margin: '9px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {v.targets.map(tg => {
            const k = tg.state === 'done' ? { icon: 'circle-check', fg: LOOK.green.fg, label: t('bgr_t_done') }
              : tg.state === 'current' ? { icon: 'circle-check', fg: 'var(--text-tertiary)', label: t('bgr_t_current') }
              : tg.state === 'running' ? { icon: 'loader-2 mf-spin', fg: LOOK.purple.fg, label: w.step(tg.step) }
              : tg.state === 'failed' ? { icon: 'circle-x', fg: LOOK.red.fg, label: t('bgr_t_failed') }
              : tg.state === 'held' ? { icon: 'player-pause', fg: LOOK.amber.fg, label: t('bgr_t_held') }
              // Once the deploy has ended, a system it never reached is not "waiting" for anything — unless a night round
              // that went well still has it to do.
              : ended && !(v.state === 'done' && v.remaining > 0) ? { icon: 'circle-minus', fg: 'var(--text-tertiary)', label: t('bgr_t_not_updated') }
              : { icon: 'circle', fg: 'var(--text-tertiary)', label: t('bgr_t_pending') };
            const why = tg.code && w.exists(`bgr_why_${tg.code}`) ? t(`bgr_why_${tg.code}`) : tg.error;
            return (
              <li key={tg.name} title={why || undefined} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                <i className={`ti ti-${k.icon}`} style={{ color: k.fg, fontSize: 14, flexShrink: 0 }} aria-hidden />
                <b style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{tg.name}</b>
                <span style={{ color: k.fg }}>{k.label}</span>
              </li>
            );
          })}
        </ul>
      )}

      {(canRetry || (running && v.mode === 'now' && !!cur && (info.can.stop || !!v.stop_requested_by_name))) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
          {canRetry && (
            <button onClick={() => { if (window.confirm(t('bgr_now_confirm', { systems: systemsOf(info, t, lang) }))) onDeployNow(); }}
              style={{ ...btn, color: LOOK.purple.fg, borderColor: `${LOOK.purple.fg}55` }}>
              <i className="ti ti-rocket" aria-hidden />{t('bgr_retry_btn')}
            </button>
          )}
          {running && v.stop_requested_by_name ? (
            <span style={{ fontSize: 12.5, fontWeight: 700 }}><i className="ti ti-player-stop" style={{ marginRight: 5 }} aria-hidden />{t('bgr_stopping', { system: stopSystem })}</span>
          ) : running && info.can.stop && cur ? (
            <button onClick={() => { if (window.confirm(t('bgr_stop_confirm', { system: stopSystem }))) onStop(v.id); }}
              style={{ ...btn, color: LOOK.red.fg, borderColor: `${LOOK.red.fg}55` }}>
              <i className="ti ti-player-stop" aria-hidden />{t('bgr_stop_btn')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ── The same, in one line across the top of the list ────────────────────────────────────────────────────────────────
const closedBanners = new Set<string>();   // per id:state, for this tab — a new state of the same deploy shows again

export function ReleaseBanner({ info, onOpen }: { info: ReleaseInfo | null; onOpen: (itemId: string) => void }) {
  const w = useWords();
  const { t } = w;
  const [, setClosed] = useState(0);
  const v = info?.current || null;
  useTick(!!v && isActive(v));
  if (!info || !v || v.state === 'cancelled' || closedBanners.has(`${v.id}:${v.state}`)) return null;

  const look = lookOf(v);
  const r = runningTarget(v);
  const running = v.state === 'running';
  const line = running
    ? (r ? t('bgr_banner_running', { system: r.name, step: w.step(r.step || v.step) }) : headline(v, info, w, false))
    : v.state === 'requested' ? t('bgr_banner_waiting')
    : v.state === 'held' ? (v.mode === 'night' ? headline(v, info, w, false) : t('bgr_banner_waiting'))
    : v.state === 'done' ? (v.deployed?.length && !(v.remaining > 0) ? t('bgr_banner_done', { names: w.names(v.deployed) }) : headline(v, info, w, false))
    : v.state === 'failed' ? (v.failed_target ? t('bgr_banner_failed', { system: v.failed_target }) : t('bgr_banner_failed_early'))
    : v.state === 'lost' ? t('bgr_banner_failed', { system: v.current_target || t('bgr_systems_unknown_mid') })
    : v.state === 'blocked' ? t('bgr_banner_blocked', { reason: w.reason(v) })
    : headline(v, info, w, false);
  const pct = running ? shownPct(v) : v.state === 'done' ? 100 : null;
  const first = (v.item_ids || [])[0];

  return (
    <div style={{ flexShrink: 0, background: look.bg, color: look.fg, borderBottom: `1px solid ${look.fg}33` }}>
      <div aria-live="polite" style={srOnly}>{line}</div>
      {/* One line on a desk; on a phone the sentence (which system, which step) gets its own line — see global.css. */}
      <div className="bgr-banner-row">
        <i className={`ti ti-${isActive(v) ? 'loader-2 mf-spin' : v.state === 'done' ? 'circle-check' : v.state === 'failed' || v.state === 'lost' ? 'alert-octagon' : 'player-pause'}`} style={{ fontSize: 15, flexShrink: 0 }} aria-hidden />
        <span className="bgr-banner-text" title={line}>{line}</span>
        <span aria-hidden className="bgr-banner-meta" style={{ flexShrink: 0, fontSize: 12, fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>
          {running ? `${Math.floor(pct || 0)}% · ` : ''}{fmtElapsed(elapsedSecs(v) * 1000)}
        </span>
        {first && (
          <button onClick={() => onOpen(first)} style={{ ...btn, padding: '3px 10px', fontSize: 12, color: look.fg, borderColor: `${look.fg}55`, background: 'transparent' }}>
            {t('bgr_banner_open')}
          </button>
        )}
        <button onClick={() => { closedBanners.add(`${v.id}:${v.state}`); setClosed(x => x + 1); }} aria-label={t('sysup_dismiss')} title={t('sysup_dismiss')}
          style={{ flexShrink: 0, width: 24, height: 24, border: 'none', borderRadius: 6, background: 'transparent', color: look.fg, cursor: 'pointer' }}>
          <i className="ti ti-x" aria-hidden />
        </button>
      </div>
      {(isActive(v) || v.state === 'done') && <Bar slim look={look} pct={pct} label={t('bgr_progress_label')} valueText={line} />}
    </div>
  );
}
